(() => {
  const componentName = 'vl-kol-video-carousel';

  if (customElements.get(componentName)) return;

  class VLKOLVideoCarousel extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.isInitialized = false;
      this.abortController = null;
      this.visibilityObserver = null;
      this.isNearViewport = false;
      this.scrollFrame = null;
      this.handleCarouselState = this.handleCarouselState.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.syncActiveVideo = this.syncActiveVideo.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.carousel = this.querySelector('vl-native-scroll-carousel');
      this.viewport = this.querySelector('[data-vl-carousel-viewport]');
      if (!this.carousel || !this.viewport) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.addEventListener('vl:carousel:ready', this.handleCarouselState, { signal });
      this.addEventListener('vl:carousel:change', this.handleCarouselState, { signal });
      this.viewport.addEventListener('scroll', this.handleScroll, { passive: true, signal });
      this.isInitialized = true;
      this.activeIndex = Number.parseInt(this.carousel.dataset.selectedIndex || '0', 10) || 0;
      this.setupVisibilityObserver();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.visibilityObserver?.disconnect();
      if (this.scrollFrame !== null) cancelAnimationFrame(this.scrollFrame);
      this.pauseAndResetAll();
      this.abortController = null;
      this.visibilityObserver = null;
      this.isNearViewport = false;
      this.scrollFrame = null;
      this.carousel = null;
      this.viewport = null;
      this.isInitialized = false;
    }

    handleCarouselState(event) {
      if (event.target !== this.carousel) return;
      this.activeIndex = Number.parseInt(String(event.detail?.index ?? 0), 10) || 0;
      this.queueSync();
    }

    handleScroll() {
      this.queueSync();
    }

    queueSync() {
      if (this.scrollFrame !== null) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(this.syncActiveVideo);
    }

    syncActiveVideo() {
      this.scrollFrame = null;
      if (!this.viewport || !this.isNearViewport) {
        this.pauseAndResetAll();
        return;
      }

      const slides = Array.from(this.querySelectorAll('[data-vl-carousel-slide]'));
      const originals = slides.filter((slide) => slide.dataset.vlCarouselClone !== 'true');
      const matchingSlides = slides.filter((slide) => this.getLogicalIndex(slide, originals) === this.activeIndex);
      const viewportLeft = this.viewport.getBoundingClientRect().left;
      const visibleSlide = matchingSlides.reduce((nearest, slide) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - viewportLeft);
        return !nearest || distance < nearest.distance ? { slide, distance } : nearest;
      }, null)?.slide || null;

      slides.forEach((slide) => {
        if (slide.dataset.vlCarouselClone === 'true') {
          slide.classList.toggle('is-active', slide === visibleSlide);
        }

        const video = slide.querySelector('video');
        if (!video) return;
        if (slide === visibleSlide) {
          const playResult = video.play();
          playResult?.catch?.(() => {});
          return;
        }

        video.pause();
        try {
          video.currentTime = 0;
        } catch (_) {
          // 部分浏览器会在媒体元数据尚未就绪时拒绝设置 currentTime。
        }
      });
    }

    setupVisibilityObserver() {
      if (!('IntersectionObserver' in window)) {
        this.isNearViewport = true;
        this.queueSync();
        return;
      }

      this.visibilityObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        this.isNearViewport = Boolean(entry?.isIntersecting);
        if (this.isNearViewport) this.queueSync();
        else this.pauseAndResetAll();
      }, { rootMargin: '600px 0px', threshold: 0.01 });
      this.visibilityObserver.observe(this);
    }

    getLogicalIndex(slide, originals) {
      if (slide.dataset.vlCarouselClone === 'true') {
        return Number.parseInt(slide.dataset.vlCarouselLogicalIndex || '0', 10) || 0;
      }
      return originals.indexOf(slide);
    }

    pauseAndResetAll() {
      this.querySelectorAll('video').forEach((video) => {
        video.pause();
        try {
          video.currentTime = 0;
        } catch (_) {
          // 与运行时切换保持相同的未就绪媒体容错。
        }
      });
    }
  }

  customElements.define(componentName, VLKOLVideoCarousel);
})();
